import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { coll } from "@/lib/ddb";
import { useCabinetsIn, useProject } from "@/lib/ddb-hooks";
import { useBom } from "@/lib/cabinets/useBom";
import type { ProjectBom, BomMaterialLine, BomEdgeLine, BomHardwareLine } from "@/lib/cabinets/bom";
import { computeQuote, defaultValidUntil, pricingFor } from "@/lib/pricing";
import type { PricingConfig, Project, Quote } from "@/lib/types";
import { PricingEditor } from "./PricingEditor";

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const USDc = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const NUM2 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

export function BomPanel() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const numId = id ? Number(id) : null;
  const { data: project } = useProject(numId);
  const { data: cabinets } = useCabinetsIn(numId);
  const { bom, isLoading, error } = useBom(numId);
  const [creatingQuote, setCreatingQuote] = useState(false);

  if (!id || Number.isNaN(Number(id))) return <div className="empty-state">Invalid project id.</div>;
  if (isLoading) return <div className="empty-state">Computing BOM…</div>;
  if (error) return <div className="empty-state">Couldn't load BOM: {error.message}</div>;
  if (!bom) return <div className="empty-state">No data.</div>;

  const pricing  = pricingFor(project);
  const quote    = computeQuote(bom, pricing, cabinets?.length ?? 0);

  async function setPricing(next: PricingConfig) {
    if (!project) return;
    await coll("projects").update<Project>(project._id, { pricing: next });
    void qc.invalidateQueries({ queryKey: ["obj", "projects", project._id] });
  }

  async function generateQuote() {
    if (!project || !bom) return;
    setCreatingQuote(true);
    try {
      const now = new Date().toISOString();
      const materialNames: Record<string, string> = {};
      bom.materials.forEach((m) => { materialNames[String(m.materialId)] = m.materialName; });

      const existing = await coll("quotes").query<Quote>([
        { field: "projectId", op: "eq", value: project._id },
      ]);
      const nextRev = `v${existing.length + 1}`;

      const quoteId = await coll("quotes").create<Quote>({
        projectId:  project._id,
        customerId: project.customerId,
        revision:   nextRev,
        breakdown:  quote,
        materialNames,
        pricing,
        status:     "draft",
        validUntil: defaultValidUntil(),
        createdAt:  now,
        updatedAt:  now,
      });
      // Advance project status to "quoted" if it's still draft.
      if (project.status === "draft") {
        await coll("projects").update<Project>(project._id, { status: "quoted" });
        void qc.invalidateQueries({ queryKey: ["obj", "projects", project._id] });
      }
      void qc.invalidateQueries({ queryKey: ["coll", "quotes"] });
      nav(`/projects/${project._id}/quotes/${quoteId}`);
    } finally {
      setCreatingQuote(false);
    }
  }

  return (
    <div style={{ padding: "16px 24px", display: "grid", gridTemplateRows: "auto 1fr", height: "100%", gap: 16 }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Bill of Materials</h2>
        <span className="muted">·</span>
        <span className="muted">{project?.name ?? "—"}</span>
        <span style={{ flex: 1 }} />
        <button className="ghost" onClick={() => nav(`/projects/${id}/design`)}>← Back to design</button>
        <button className="ghost" onClick={() => exportCsv(bom, project?.name ?? "project")}>Export CSV</button>
        <button
          onClick={generateQuote}
          disabled={creatingQuote || (cabinets?.length ?? 0) === 0}
        >
          {creatingQuote ? "Generating…" : "Generate Quote →"}
        </button>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, alignItems: "start" }}>
        <Section
          title="Sheet goods"
          totalLabel={`${bom.materials.length} materials`}
          total={bom.totalMaterials}
          columns={["Material", "Sq ft", "Sheets", "$/sheet", "Cost"]}
          rows={bom.materials.map(materialRow)}
          empty="No materials. Add cabinets in the design view."
        />
        <Section
          title="Edge banding"
          totalLabel={`${bom.edges.length} bands`}
          total={bom.totalEdges}
          columns={["Band", "Lin ft", "$/lft", "Cost"]}
          rows={bom.edges.map(edgeRow)}
          empty="No edge banding."
        />
        <Section
          title="Hardware"
          totalLabel={`${bom.hardware.reduce((a,h) => a + h.qty, 0)} pieces`}
          total={bom.totalHardware}
          columns={["Item", "Kind", "Qty", "$/ea", "Cost"]}
          rows={bom.hardware.map(hardwareRow)}
          empty="No hardware allocated."
        />
      </div>

      <footer style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, alignItems: "stretch" }}>
        <PricingEditor value={pricing} onChange={setPricing} />
        <div className="card" style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, alignItems: "baseline" }}>
          <Stat label="Parts"      value={String(bom.partCount)} />
          <Stat label="Cabinets"   value={String(quote.cabinetCount)} />
          <Stat label="Materials"  value={USD.format(bom.totalMaterials)} />
          <Stat label="Hardware+Edges" value={USD.format(bom.totalEdges + bom.totalHardware)} />
          <Stat label="Labor"      value={USD.format(quote.laborCost)} />
          <Stat label={`Markup (${(pricing.markup * 100).toFixed(0)}%)`} value={USD.format(quote.markupAmount)} />
          <Stat label={`Tax (${(pricing.taxRate * 100).toFixed(1)}%)`}   value={USD.format(quote.taxAmount)} />
          <Stat label="Delivery"   value={USD.format(quote.delivery)} />
          <div style={{ gridColumn: "1 / -1", borderTop: "1px solid var(--line)", paddingTop: 12, display: "flex", justifyContent: "flex-end", alignItems: "baseline", gap: 12 }}>
            <span className="muted">Customer total:</span>
            <span style={{ fontSize: 26, fontWeight: 700, color: "var(--accent)" }}>
              {USD.format(quote.total)}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── Cell renderers ───────────────────────────────────────────────

type Row = (string | number)[];

function materialRow(m: BomMaterialLine): Row {
  return [m.materialName, NUM2.format(m.sqFt), m.sheets, USDc.format(m.unitCost), USD.format(m.cost)];
}
function edgeRow(e: BomEdgeLine): Row {
  return [e.edgeName, NUM2.format(e.lft), USDc.format(e.unitCost), USD.format(e.cost)];
}
function hardwareRow(h: BomHardwareLine): Row {
  return [h.hardwareName, h.kind.replace("_", " "), h.qty, USDc.format(h.unitCost), USD.format(h.cost)];
}

// ── Section ──────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  totalLabel: string;
  total: number;
  columns: string[];
  rows: Row[];
  empty: string;
}

function Section({ title, totalLabel, total, columns, rows, empty }: SectionProps) {
  return (
    <section className="card" style={{ padding: 0 }}>
      <header style={{ padding: "12px 16px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "baseline", gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</h3>
        <span className="muted" style={{ fontSize: 11 }}>{totalLabel}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontWeight: 600 }}>{USD.format(total)}</span>
      </header>
      {rows.length === 0 ? (
        <div className="muted" style={{ padding: 16, fontSize: 13 }}>{empty}</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th
                  key={c}
                  style={{
                    textAlign: i === 0 ? "left" : "right",
                    padding: "6px 12px",
                    borderBottom: "1px solid var(--line)",
                    color: "var(--fg-dim)",
                    fontWeight: 500,
                  }}
                >{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    style={{
                      textAlign: ci === 0 ? "left" : "right",
                      padding: "6px 12px",
                      whiteSpace: "nowrap",
                      fontVariantNumeric: ci > 0 ? "tabular-nums" : "normal",
                    }}
                  >{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Stat({ label, value, big }: { label: string; value: string; big?: boolean }) {
  return (
    <div>
      <div className="label">{label}</div>
      <div style={{ fontSize: big ? 22 : 16, fontWeight: big ? 700 : 600, color: big ? "var(--accent)" : "var(--fg)" }}>{value}</div>
    </div>
  );
}

// ── CSV export ───────────────────────────────────────────────────

function exportCsv(bom: ProjectBom, projectName: string) {
  const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
  const lines: string[] = [];
  lines.push("section,name,kind,qty,unit,unit_cost,total_cost");
  for (const m of bom.materials) {
    lines.push([
      "material", esc(m.materialName), "sheet",
      m.sheets, "sheet", m.unitCost.toFixed(2), m.cost.toFixed(2),
    ].join(","));
  }
  for (const e of bom.edges) {
    lines.push([
      "edge", esc(e.edgeName), "band",
      e.lft.toFixed(1), "lft", e.unitCost.toFixed(2), e.cost.toFixed(2),
    ].join(","));
  }
  for (const h of bom.hardware) {
    lines.push([
      "hardware", esc(h.hardwareName), h.kind,
      h.qty, "ea", h.unitCost.toFixed(2), h.cost.toFixed(2),
    ].join(","));
  }
  lines.push(["","TOTAL","","","",bom.grandTotal.toFixed(2),""].join(","));

  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${projectName.replace(/\W+/g, "_")}_bom.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
